/**
 * The place being managed is the first segment of the panel's URL:
 * `/<slug>/<section>` — `/junglerock/info`, `/tlom/stages`, `/tlom-1/rounds/12`.
 *
 * It used to sit in localStorage, which made every link ambiguous: a URL
 * pasted to a colleague opened *their* last gym, and an email pointing at
 * `/members/9/edit` opened member 9's page on whichever place the reader had
 * last been managing. With the slug in the path a link says where it goes.
 *
 * These are the pure pieces: what the context and the guards do with them is
 * theirs to decide.
 */

/** A path split into the place it names and the section under it. */
export interface PlacePath {
  /** The first segment, or null at the root. Not validated: that is the context's job. */
  slug: string | null;
  /** Everything after the slug, leading slash included; '' at the place's root. */
  sectionPath: string;
}

export function splitPlacePath(pathname: string): PlacePath {
  const [slug = '', ...rest] = pathname.replace(/^\/+/, '').split('/');
  if (!slug) return { slug: null, sectionPath: '' };

  const sectionPath = rest.join('/').replace(/\/+$/, '');
  return { slug, sectionPath: sectionPath ? `/${sectionPath}` : '' };
}

/**
 * The section a path is in, without the record it is open on: `/rounds/12` is
 * the rounds section. This is what survives a switch to another place — the
 * section carries over, the record does not, since it was the old place's.
 */
export function sectionRoot(sectionPath: string): string {
  const [first] = sectionPath.replace(/^\/+/, '').split('/');
  return first ? `/${first}` : '';
}

/** The same section, at another place. */
export function placePath(slug: string, sectionPath: string): string {
  return `/${slug}${sectionPath}`;
}
